/**
 * Hive Flows editor store — the canvas graph state (Zustand).
 *
 * Holds the @xyflow nodes/edges plus editor UX state (selection, the
 * Essenziale/Comune/Avanzato mode tier, dirty flag). Converts to/from the BE
 * ``FlowGraph`` wire shape so the API layer never sees xyflow internals.
 */

import type { FlowGraph, NodePaletteItem } from "@ando/core";
import {
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
} from "@xyflow/react";
import { create } from "zustand";

export type ModeTier = "essential" | "common" | "advanced";

/** Data carried on every xyflow node. */
export interface FlowNodeData {
  nodeType: string;
  label: string;
  category: NodePaletteItem["category"];
  icon: string;
  riskLevel: NodePaletteItem["risk_level"];
  config: Record<string, unknown>;
  [key: string]: unknown;
}

export type FlowEditorNode = Node<FlowNodeData>;

function newId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `n_${crypto.randomUUID().slice(0, 8)}`;
  }
  return `n_${Math.random().toString(36).slice(2, 10)}`;
}

interface FlowEditorState {
  nodes: FlowEditorNode[];
  edges: Edge[];
  selectedNodeId: string | null;
  modeTier: ModeTier;
  dirty: boolean;
  graphMeta: Pick<FlowGraph, "surface" | "run_form">;

  onNodesChange: (changes: NodeChange<FlowEditorNode>[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (conn: Connection) => void;

  addNode: (spec: NodePaletteItem, position: { x: number; y: number }) => string;
  updateNodeConfig: (nodeId: string, config: Record<string, unknown>) => void;
  removeNode: (nodeId: string) => void;
  duplicateNode: (nodeId: string) => void;
  selectNode: (nodeId: string | null) => void;
  setModeTier: (tier: ModeTier) => void;
  setSurface: (surface: FlowGraph["surface"]) => void;

  loadGraph: (graph: FlowGraph) => void;
  hydrateVisuals: (byType: ReadonlyMap<string, NodePaletteItem>) => void;
  toGraph: () => FlowGraph;
  reset: () => void;
  markSaved: () => void;
}

export const useFlowEditorStore = create<FlowEditorState>((set, get) => ({
  nodes: [],
  edges: [],
  selectedNodeId: null,
  modeTier: "essential",
  dirty: false,
  graphMeta: {},

  onNodesChange: (changes) =>
    set((s) => ({ nodes: applyNodeChanges(changes, s.nodes), dirty: true })),
  onEdgesChange: (changes) =>
    set((s) => ({ edges: applyEdgeChanges(changes, s.edges), dirty: true })),
  onConnect: (conn) =>
    set((s) => {
      // A logic.if node exposes "true"/"false" source handles — carry the
      // chosen branch onto the edge so the BE prunes the untaken path.
      const branch =
        conn.sourceHandle === "true" || conn.sourceHandle === "false"
          ? conn.sourceHandle
          : undefined;
      const edge = {
        ...conn,
        type: "smoothstep",
        ...(branch ? { label: branch === "true" ? "Sì" : "No", data: { condition: branch } } : {}),
      };
      return { edges: addEdge(edge, s.edges), dirty: true };
    }),

  addNode: (spec, position) => {
    const id = newId();
    const node: FlowEditorNode = {
      id,
      type: "flowNode",
      position,
      data: {
        nodeType: spec.node_type,
        label: spec.label,
        category: spec.category,
        icon: spec.icon,
        riskLevel: spec.risk_level,
        config: {},
      },
    };
    set((s) => ({ nodes: [...s.nodes, node], selectedNodeId: id, dirty: true }));
    return id;
  },

  updateNodeConfig: (nodeId, config) =>
    set((s) => ({
      nodes: s.nodes.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, config } } : n)),
      dirty: true,
    })),

  removeNode: (nodeId) =>
    set((s) => ({
      nodes: s.nodes.filter((n) => n.id !== nodeId),
      edges: s.edges.filter((e) => e.source !== nodeId && e.target !== nodeId),
      selectedNodeId: s.selectedNodeId === nodeId ? null : s.selectedNodeId,
      dirty: true,
    })),

  duplicateNode: (nodeId) =>
    set((s) => {
      const src = s.nodes.find((n) => n.id === nodeId);
      if (!src) return {};
      const id = newId();
      const copy: FlowEditorNode = {
        ...src,
        id,
        selected: false,
        position: { x: src.position.x + 40, y: src.position.y + 64 },
        data: { ...src.data, config: { ...src.data.config } },
      };
      return { nodes: [...s.nodes, copy], selectedNodeId: id, dirty: true };
    }),

  selectNode: (nodeId) => set({ selectedNodeId: nodeId }),
  setModeTier: (tier) => set({ modeTier: tier }),
  setSurface: (surface) =>
    set((s) => ({ graphMeta: { ...s.graphMeta, surface }, dirty: true })),

  loadGraph: (graph) =>
    set({
      graphMeta: { surface: graph.surface, run_form: graph.run_form },
      nodes: (graph.nodes ?? []).map((n) => ({
        id: n.id,
        type: "flowNode",
        position: n.position ?? { x: 0, y: 0 },
        data: {
          nodeType: n.node_type,
          // label/category/icon/risk are re-hydrated from the palette in the
          // canvas (kept off the wire); placeholders here until then.
          label: n.node_type,
          category: "action",
          icon: "circle",
          riskLevel: "low",
          config: n.config ?? {},
        },
      })),
      edges: (graph.edges ?? []).map((e, i) => {
        const branch = e.condition === "true" || e.condition === "false";
        return {
          id: `e_${i}_${e.source}_${e.target}`,
          source: e.source,
          target: e.target,
          type: "smoothstep",
          // Reconnect branch edges to their "true"/"false" source handle.
          ...(branch
            ? {
                sourceHandle: e.condition,
                label: e.condition === "true" ? "Sì" : "No",
                data: { condition: e.condition },
              }
            : e.condition
              ? { label: e.condition, data: { condition: e.condition } }
              : {}),
        };
      }),
      selectedNodeId: null,
      dirty: false,
    }),

  // After ``loadGraph`` (which only knows node_type), re-hydrate each node's
  // visuals (label/category/icon/risk) from the palette spec — the catalog
  // lives only on the BE, so it's never persisted on the wire.
  hydrateVisuals: (byType) =>
    set((s) => ({
      nodes: s.nodes.map((n) => {
        const spec = byType.get(n.data.nodeType);
        if (!spec) return n;
        return {
          ...n,
          data: {
            ...n.data,
            label: spec.label,
            category: spec.category,
            icon: spec.icon,
            riskLevel: spec.risk_level,
          },
        };
      }),
    })),

  toGraph: () => {
    const { nodes, edges, graphMeta } = get();
    return {
      ...graphMeta,
      nodes: nodes.map((n) => ({
        id: n.id,
        node_type: n.data.nodeType,
        config: n.data.config,
        position: { x: Math.round(n.position.x), y: Math.round(n.position.y) },
      })),
      edges: edges.map((e) => ({
        source: e.source,
        target: e.target,
        condition: (e.data as { condition?: string } | undefined)?.condition ?? null,
      })),
    };
  },

  reset: () => set({ nodes: [], edges: [], graphMeta: {}, selectedNodeId: null, dirty: false }),
  markSaved: () => set({ dirty: false }),
}));
